import { Settings, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { useCashier } from "./CashierProvider";
import { DepositPane } from "./DepositPane";
import { WithdrawPane } from "./WithdrawPane";

const FOCUSABLE =
  'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

export function CashierDialog() {
  const {
    isOpen,
    closeCashier,
    flow,
    setFlow,
    state,
    message,
    selectedWallet,
    totalBalance,
    wallets,
    busy,
    selectWallet,
  } = useCashier();
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  // Takeover mode while a PSP iframe is mounted: the panel goes full
  // viewport (mobile) / 880px (desktop) and the chrome hides so the
  // checkout has room to breathe (donor CashierApp behaviour). Closing the
  // dialog unmounts the panes, whose effect cleanup resets this to false.
  const [fullscreen, setFullscreen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const handleFullscreen = useCallback((next: boolean) => {
    setFullscreen(next);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.requestAnimationFrame(() => closeRef.current?.focus());

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeCashier();
        return;
      }
      if (event.key !== "Tab" || !panelRef.current) return;
      const focusable = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE),
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable.at(-1);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [closeCashier, isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div
      aria-label={fullscreen ? "Cashier" : undefined}
      aria-labelledby={fullscreen ? undefined : "cashier-title"}
      aria-modal="true"
      className="cashier-dialog"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) closeCashier();
      }}
      role="dialog"
    >
      <div
        className="cashier-dialog__panel"
        data-flow={flow}
        data-fullscreen={fullscreen || undefined}
        ref={panelRef}
      >
        {fullscreen ? null : (
          <>
            <div className="cashier-dialog__handle" aria-hidden />
            <header className="cashier-dialog__header">
              <span className="cashier-dialog__title-icon">
                <img
                  alt=""
                  aria-hidden
                  src="/app/static/themes/monkeytilt/icons/cashier.svg"
                />
              </span>
              <h2 id="cashier-title">Cashier</h2>
              <button
                aria-expanded={settingsOpen}
                aria-label="Wallet settings"
                className="cashier-dialog__settings"
                onClick={() => setSettingsOpen((value) => !value)}
                type="button"
              >
                <Settings aria-hidden />
              </button>
              <button
                ref={closeRef}
                aria-label="Close Cashier"
                className="cashier-dialog__close"
                onClick={closeCashier}
                type="button"
              >
                <X aria-hidden />
              </button>
              {settingsOpen ? (
                <div
                  aria-label="Wallet balances"
                  className="cashier-dialog__wallet-menu"
                  role="menu"
                >
                  <strong>Wallets</strong>
                  {wallets.map((wallet) =>
                    wallet.readOnly ? (
                      <div
                        aria-current={
                          wallet.id === selectedWallet?.id || undefined
                        }
                        className="cashier-dialog__wallet-option"
                        key={wallet.id}
                        role="menuitem"
                      >
                        <span>{wallet.currency}</span>
                        <b>{wallet.total}</b>
                      </div>
                    ) : (
                      <button
                        aria-current={
                          wallet.id === selectedWallet?.id || undefined
                        }
                        className="cashier-dialog__wallet-option"
                        disabled={busy}
                        key={wallet.id}
                        onClick={() => {
                          setSettingsOpen(false);
                          void selectWallet(wallet.id);
                        }}
                        role="menuitem"
                        type="button"
                      >
                        <span>{wallet.currency}</span>
                        <b>{wallet.total}</b>
                      </button>
                    ),
                  )}
                </div>
              ) : null}
            </header>

            <div aria-label="Cashier actions" className="cashier-dialog__tabs">
              <button
                aria-selected={flow === "deposit"}
                className="is-deposit"
                onClick={() => {
                  setSettingsOpen(false);
                  setFlow("deposit");
                }}
                role="tab"
                type="button"
              >
                Deposit
              </button>
              <button
                aria-selected={flow === "withdraw"}
                className="is-withdraw"
                onClick={() => {
                  setSettingsOpen(false);
                  setFlow("withdraw");
                }}
                role="tab"
                type="button"
              >
                Withdraw
              </button>
            </div>

            <section
              aria-label="Current wallet balance"
              className="cashier-balance-card"
              data-state={state}
            >
              {selectedWallet ? (
                <>
                  <div className="cashier-balance-card__total">
                    <strong>{totalBalance ?? selectedWallet.total}</strong>
                    <span>{selectedWallet.currency}</span>
                  </div>
                  {flow === "withdraw" && selectedWallet.cash ? (
                    <div className="cashier-balance-card__withdrawable">
                      <span>↑ Withdrawable</span>
                      <strong>{selectedWallet.cash}</strong>
                    </div>
                  ) : null}
                </>
              ) : (
                <div className="cashier-balance-card__empty">
                  <strong>{message ?? "Cashier unavailable"}</strong>
                  <span>No wallet or balance has been invented.</span>
                </div>
              )}
            </section>
          </>
        )}

        {flow === "deposit" ? (
          <DepositPane key="deposit" onFullscreen={handleFullscreen} />
        ) : (
          <WithdrawPane key="withdraw" onFullscreen={handleFullscreen} />
        )}
      </div>
    </div>,
    document.body,
  );
}
